import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { PerformanceGameRewardModes } from "kadow-jetpack-model";
import { tt } from "../../../../utils/i18n/i18n";
import { useJetpackConfigSelector } from "../../../../redux/jetpackConfig";
import NiceModal from "@ebay/nice-modal-react";
import CreateAnimationModal, {
  CreateAnimationStep
} from "../../../../components/modal/ConfigAnimationModal/CreateAnimationModal";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import GameConfigUpsell from "../UpsellComponents/GameConfigUpsell";
import KC_Padder from "../../../../designsystem/KC_Padder/KC_Padder";
import EventSuggestion from "./EventSuggestions";
import GameListView from "./gameList/GameListView";
import {
  GAME_LIST_PARAM,
  GAME_LIST_WRAP_PARAM,
  isWrapEnabled,
  resolveGameListMode
} from "./gameList/gameListTypes";

export default function GamesConfigurator() {
  const jpConfig = useJetpackConfigSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const location = useLocation();

  const { gameListMode, wrapGameList } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const mode = resolveGameListMode(params.get(GAME_LIST_PARAM));
    return {
      gameListMode: mode,
      wrapGameList:
        mode === "default" && isWrapEnabled(params.get(GAME_LIST_WRAP_PARAM))
    };
  }, [location.search]);

  const canUseGamesOfChance = useMemo(() => {
    return !!accountToDisplay?.saasFeatures?.gc;
  }, [accountToDisplay]);

  const canUsePerformanceGames = useMemo(() => {
    const monthlyLotteryEnabledOrPhysicalReward =
      !jpConfig?.dml || jpConfig?.pgrm === PerformanceGameRewardModes.Prize;
    return (
      monthlyLotteryEnabledOrPhysicalReward &&
      !!accountToDisplay?.saasFeatures?.pg
    );
  }, [jpConfig?.dml, jpConfig?.pgrm, accountToDisplay?.saasFeatures]);

  const displayEventSuggestion = useMemo(() => {
    return !!accountToDisplay?.saasFeatures?.pg;
  }, [accountToDisplay?.saasFeatures]);

  const showCreateAnimationModal = async (step: CreateAnimationStep) => {
    await NiceModal.show(CreateAnimationModal, {
      title: tt("create_new_animation"),
      step,
      includePerformanceGames: canUsePerformanceGames,
      includeGamesOfChance: canUseGamesOfChance
    });
  };

  const UPSELL = useMemo(() => {
    if (!canUseGamesOfChance || !canUsePerformanceGames) {
      return (
        <GameConfigUpsell
          includeGamesOfChance={!canUseGamesOfChance}
          includePerformanceGames={!canUsePerformanceGames}
          includeSkins={true}
          config={jpConfig}
        />
      );
    }

    return null;
  }, [canUseGamesOfChance, canUsePerformanceGames, jpConfig]);

  return (
    <div className="dashboard-v2">
      <GameListView
        mode={gameListMode}
        wrap={wrapGameList}
        canUseGamesOfChance={canUseGamesOfChance}
        canUsePerformanceGames={canUsePerformanceGames}
        showCreateAnimationModal={showCreateAnimationModal}
      />
      {displayEventSuggestion && (
        <div className="px-4">
          <EventSuggestion />
        </div>
      )}
      <div className="mt-4 px-4">{UPSELL}</div>
      <KC_Padder />
    </div>
  );
}
